import { useContext, useState } from 'react';
import { Button, makeStyles, CircularProgress, Backdrop } from '@material-ui/core';
import MyDataSpacesViewContext from '../../context/MyDataSpacesViewContext';
import SnackbarAlertContext from '../../../../context/SnackbarAlertContext';
import { UserProfileContext } from '../../../../UserProfileContext';
import { updateDataSpace_v2 } from '../../../../api_helper/api';
import { trackEvent } from '../../../../utils/eventTracking';
import EditDataSpaceFormContext from '../../context/EditDataSpaceFormContext';
import { getUserMessage } from '../../../../utils/handleError';

const useStyles = makeStyles((theme) => ({
	loadingOverlay: {
		width: '100vw',
		height: '100vh',
		zIndex: '100',
		color: '#FFFFFF',
	},
}));

// Button which creates dataspace when clicked.
const CreateDataSpaceButton = () => {
	const classes = useStyles();

	// User Profile Data
	const userProfile = useContext(UserProfileContext)[0];
	const editDataSpaceFormContext = useContext(EditDataSpaceFormContext);

	const myDataSpacesViewContext = useContext(MyDataSpacesViewContext);
	const snackbarAlertContext = useContext(SnackbarAlertContext);

	// Used to determine when to render circular loading animation overlay.
	const [isLoading, setIsLoading] = useState(false);

	const validateNewDataSpace = () => {
		// Check that an anchor is selected for the dataspace.
		if (editDataSpaceFormContext.dataSpace.joinData.every((dataset) => dataset.isAnchor === false)) {
			return {
				isNewDataSpaceValid: false,
				msg: 'Please select an anchor for your data space.',
			};
		}

		// If there is more than one selected dataset, check that all selected datasources have at least one join.
		if (editDataSpaceFormContext.dataSpace.datasets.length > 1) {
			for (let dataset of editDataSpaceFormContext.dataSpace.joinData) {
				if (dataset.joins.length === 0) {
					return {
						isNewDataSpaceValid: false,
						msg:
							'Please create a join for "' +
							`${dataset.displayName || dataset.name} ${dataset.tags.timeFrame || ''}` +
							` (${dataset._id.substring(21)})` +
							'" or remove the dataset.',
					};
				}
			}
		}

		return {
			isNewDataSpaceValid: true,
		};
	};

	// When save dataspace is clicked.
	const updateDataSpaceClickHandler = async () => {
		try {
			trackEvent({
				userDetails: { userId: userProfile._id, email: userProfile.email },
				eventDetails: {
					types: ['KissMetrics', 'AppInsights', 'Segment', 'GA4'],
					eventName: 'WebApp_User Clicks Save DataSpace Button In Edit DataSpace Form',
				},
			});

			const validateNewDataSpaceRes = validateNewDataSpace();

			if (!validateNewDataSpaceRes.isNewDataSpaceValid) {
				snackbarAlertContext.setSnackbarAlert({
					isSnackbarOpen: true,
					autohide: 5000,
					severity: 'error',
					msg: validateNewDataSpaceRes.msg,
				});
				return;
			}

			// Render circular loading animation overlay while dataspace is being created.
			setIsLoading(true);

			const updatedDataSpace = await updateDataSpace_v2({
				dataSpaceId: editDataSpaceFormContext.dataSpace._id,
				updatedFields: { dataSpace: editDataSpaceFormContext.dataSpace },
			});

			myDataSpacesViewContext.setMyDataSpacesView((prevState) => ({
				...prevState,
				selectedDataSpace: {
					name: updatedDataSpace.name,
					id: updatedDataSpace._id,
				},
			}));

			setIsLoading(false);
			myDataSpacesViewContext.changeViewHandler('dataSpaceViewNew');
			return;
		} catch (e) {
			setIsLoading(false);
			snackbarAlertContext.setSnackbarAlert({ msg: getUserMessage(e), severity: 'error' });
			return;
		}
	};

	return (
		<>
			<Button variant="contained" margin="normal" color="primary" onClick={updateDataSpaceClickHandler}>
				Save DataSpace
			</Button>
			<Backdrop open={isLoading} className={classes.loadingOverlay}>
				<CircularProgress color="inherit" />
			</Backdrop>
		</>
	);
};

export default CreateDataSpaceButton;
